import React, { useState, useEffect } from "react";
import axios from "axios";
import { Package, RefreshCw, ExternalLink, Truck } from "lucide-react";
import { isAuthError, redirectToSafeLogin } from "../authHeaders";

/* -------------------------------------------------------------------------- */
/* ✅ Order History — isolated tab component                                   */
/* Fetches the logged-in customer's Shopify orders via the secure backend      */
/* route (GET /api/customer-orders?email=...). Never touches Shopify tokens.   */
/* -------------------------------------------------------------------------- */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/* Small helpers — kept local so nothing outside this file is touched */
function formatDate(iso) {
  try {
    return iso ? new Date(iso).toLocaleDateString() : "—";
  } catch {
    return "—";
  }
}

function formatPrice(total, currency) {
  try {
    const value = parseFloat(total);
    if (Number.isNaN(value)) return total || "—";
    return `${currency === "USD" ? "$" : currency + " "}${value.toFixed(2)}`;
  } catch {
    return total || "—";
  }
}

/* Status badge styling — matches portal red/neutral token palette */
function statusClasses(status) {
  switch ((status || "").toLowerCase()) {
    case "paid":
    case "fulfilled":
      return "bg-green-100 text-green-800";
    case "pending":
    case "partially_fulfilled":
    case "in_progress":
      return "bg-yellow-100 text-yellow-800";
    case "refunded":
    case "unfulfilled":
      return "bg-gray-200 text-gray-700";
    case "voided":
    case "cancelled":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

/* Shipment status badge styling — delivery-focused (e.g. "Delivered") */
function shipmentStatusClasses(status) {
  switch ((status || "").toLowerCase()) {
    case "delivered":
      return "bg-green-100 text-green-800";
    case "out_for_delivery":
      return "bg-blue-100 text-blue-800";
    case "in_transit":
    case "picked_up":
      return "bg-yellow-100 text-yellow-800";
    case "failure":
    case "returned_to_sender":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

function StatusBadge({ status, variant }) {
  const label = (status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const classes =
    variant === "shipment"
      ? shipmentStatusClasses(status)
      : statusClasses(status);
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[12px] font-medium ${classes}`}
    >
      {label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* ✅ Single Order Row / Card                                                  */
/* -------------------------------------------------------------------------- */
function OrderRow({ order }) {
  return (
    <div className="border border-gray-200 rounded-xl p-5 mb-4 shadow-sm hover:shadow-md transition-all duration-200 max-md:p-4">
      {/* Header: order name / date + total — stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <p className="text-[16px] font-semibold text-gray-900">
            Order {order.name}
          </p>
          <p className="text-[13px] text-gray-500">
            Placed {formatDate(order.date)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-[16px] font-semibold text-gray-900">
            {formatPrice(order.totalPrice, order.currency)}
          </p>
          {/* {order.trackingUrl && (
            <a
              href={order.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#da291c] hover:underline text-[13px] flex items-center gap-1"
            >
              View Status <ExternalLink size={13} />
            </a>
          )} */}
        </div>
      </div>

      {/* Statuses */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <StatusBadge status={order.financialStatus} />
        <StatusBadge status={order.fulfillmentStatus} />
        {order.shipmentStatus && (
          <StatusBadge status={order.shipmentStatus} variant="shipment" />
        )}
        {order.trackingNumber && (
          <span className="text-[12px] text-gray-500 flex items-center gap-1">
            <Truck size={13} /> {order.trackingCompany || "Tracking"}:{" "}
            {order.trackingNumber}
          </span>
        )}
      </div>

      {/* Items */}
      {Array.isArray(order.items) && order.items.length > 0 ? (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {order.items.map((item) => (
            <li
              key={item.id}
              className="py-2.5 flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <span className="text-[14px] text-gray-700">
                  {item.title}{" "}
                  <span className="text-gray-500">× {item.quantity}</span>
                </span>
                {/* Per-item tracking info — same style as the order-level tracking */}
                {item.trackingInfo &&
                  (item.trackingInfo.number || item.trackingInfo.url) && (
                    <span className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-[12px] text-gray-500 flex items-center gap-1">
                        <Truck size={13} />{" "}
                        {item.trackingInfo.company || "Tracking"}
                        {item.trackingInfo.number
                          ? `: ${item.trackingInfo.number}`
                          : ""}
                      </span>
                      {item.trackingInfo.url && (
                        <a
                          href={item.trackingInfo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#da291c] hover:underline text-[12px] flex items-center gap-1"
                        >
                          View Status <ExternalLink size={12} />
                        </a>
                      )}
                    </span>
                  )}
              </div>
              <span className="text-[14px] text-gray-700 whitespace-nowrap">
                {parseFloat(item.price) > 0
                  ? formatPrice(item.price, order.currency)
                  : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-gray-500 border-t border-gray-100 pt-3">
          No line items available for this order.
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main OrderHistory Component                                              */
/* -------------------------------------------------------------------------- */
export default function OrderHistory({ email }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ORDERS_PER_PAGE = 10;

  const fetchOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${BASE_URL}/api/customer-orders`, {
        params: { email },
      });

      if (data?.success) {
        setOrders(Array.isArray(data.orders) ? data.orders : []);
      } else {
        setError(data?.error || "Could not load your orders.");
      }
    } catch (err) {
      // Auth failure (401 Unauthorized / 403 Forbidden): this component has no
      // access to the portal's auth state, so safely redirect to the
      // (unauthenticated) login page.
      if (isAuthError(err)) {
        redirectToSafeLogin();
        return;
      }
      const serverMsg = err?.response?.data?.error;
      setError(serverMsg || "Error loading your orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!email) {
      setLoading(false);
      setError("You need to be signed in to view your orders.");
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  /* Reset back to the first page whenever the orders list changes */
  useEffect(() => {
    setCurrentPage(1);
  }, [orders]);

  /* Pagination math */
  const totalPages = Math.ceil(orders.length / ORDERS_PER_PAGE);
  const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
  const paginatedOrders = orders.slice(
    startIndex,
    startIndex + ORDERS_PER_PAGE
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="h-full flex flex-col bg-white border border-gray-200 rounded-xl p-8 shadow-sm max-md:h-auto max-md:p-4">
      <h3 className="text-[18px] font-semibold text-gray-900 mb-3 flex items-center gap-2 shrink-0">
        <Package size={18} /> Order History
      </h3>
      <p className="text-[14px] text-gray-600 mb-4 shrink-0">
        All Shopify orders placed with this email address.
      </p>

      {/* Loading state — matches portal's spinner pattern */}
      {loading && (
        <div className="flex items-center justify-center py-10 shrink-0">
          <div className="w-10 h-10 border-4 border-gray-900 border-t-transparent rounded-full animate-spin-slow"></div>
        </div>
      )}

      {/* Error state with Retry */}
      {!loading && error && (
        <div className="text-center py-8 shrink-0">
          <p className="text-[14px] text-red-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={fetchOrders}
            className="bg-[#da291c] text-white px-4 py-2 rounded-md text-[14px] hover:bg-[#b71f14] transition inline-flex items-center gap-2"
          >
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && orders.length === 0 && (
        <p className="text-gray-500 text-[14px] shrink-0">
          You don't have any orders yet. Once you place an order it will appear
          here.
        </p>
      )}

      {/* Orders list — vertically scrollable, paginated 10 per page */}
      {!loading && !error && orders.length > 0 && (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 max-md:flex-none max-md:overflow-visible max-md:pr-0">
            {paginatedOrders.map((order) => (
              <OrderRow key={order.id} order={order} />
            ))}
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4 mt-2">
              <p className="text-[13px] text-gray-500">
                Showing {startIndex + 1}–
                {Math.min(startIndex + ORDERS_PER_PAGE, orders.length)} of{" "}
                {orders.length} orders
              </p>
              <div className="flex items-center gap-1 max-md:flex-wrap max-md:w-full">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-[13px] rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition max-md:min-h-[44px]"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => goToPage(page)}
                      className={`w-8 h-8 text-[13px] rounded-md border transition max-md:w-11 max-md:h-11 ${
                        page === currentPage
                          ? "bg-[#da291c] text-white border-[#da291c]"
                          : "border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-[13px] rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition max-md:min-h-[44px]"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

